import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, MessageSquare, ThumbsUp, Bot, UserPlus, Loader2, Check, CheckCheck } from 'lucide-react';
import { notificationsApi } from '../../api';

const TYPE_ICON = {
  comment_on_post: MessageSquare,
  reaction_on_post: ThumbsUp,
  ai_answered_question: Bot,
  connection_request: UserPlus,
  connection_accepted: UserPlus,
  roadmap_vote: ThumbsUp,
  roadmap_status_change: Bell,
};

function timeAgo(iso) {
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return 'teraz';
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h`;
  const d = Math.floor(h / 24);
  return `${d} d`;
}

/**
 * Bell w nav header z dropdownem nieprzeczytanych. Polling 30s żeby wyłapać
 * nowe powiadomienia (komentarz, reakcja, AI moderator answer, connection req).
 */
export default function NotificationDropdown() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const ref = useRef(null);

  const fetchCount = async () => {
    try {
      const r = await notificationsApi.unreadCount();
      setUnread(r.data?.unread_count || 0);
    } catch {}
  };

  const fetchList = async () => {
    setLoading(true);
    try {
      const r = await notificationsApi.list(false, 20);
      setItems(r.data?.items || []);
      setUnread(r.data?.unread_count || 0);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => {
    fetchCount();
    const id = setInterval(fetchCount, 30000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (open) fetchList();
  }, [open]);

  // Close on click outside
  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const handleClick = async (n) => {
    if (!n.is_read) {
      try { await notificationsApi.markRead(n.id); } catch {}
      setItems(prev => prev.map(it => it.id === n.id ? { ...it, is_read: true } : it));
      setUnread(prev => Math.max(0, prev - 1));
    }
    if (n.link) {
      setOpen(false);
      navigate(n.link);
    }
  };

  const handleMarkAll = async () => {
    try { await notificationsApi.markAllRead(); } catch {}
    setItems(prev => prev.map(it => ({ ...it, is_read: true })));
    setUnread(0);
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="relative p-2 text-gray-400 hover:text-gray-600 transition-colors"
        aria-label="Powiadomienia"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 text-[10px] font-bold bg-red-500 text-white rounded-full flex items-center justify-center">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
            <h3 className="font-semibold text-sm text-gray-900">Powiadomienia</h3>
            {unread > 0 && (
              <button onClick={handleMarkAll} className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-0.5">
                <CheckCheck className="w-3 h-3" /> Oznacz wszystkie
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading && items.length === 0 ? (
              <div className="p-6 text-center"><Loader2 className="w-5 h-5 text-indigo-500 animate-spin inline" /></div>
            ) : items.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-500">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
                Brak powiadomień
              </div>
            ) : (
              items.map(n => {
                const Icon = TYPE_ICON[n.type] || Bell;
                return (
                  <button
                    key={n.id}
                    onClick={() => handleClick(n)}
                    className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition flex items-start gap-3 ${!n.is_read ? 'bg-indigo-50/50' : ''}`}
                  >
                    <div className={`mt-0.5 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      n.type === 'ai_answered_question' ? 'bg-indigo-100 text-indigo-600' :
                      n.type === 'reaction_on_post' ? 'bg-rose-100 text-rose-600' :
                      n.type === 'comment_on_post' ? 'bg-emerald-100 text-emerald-600' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${!n.is_read ? 'font-semibold text-gray-900' : 'text-gray-700'} truncate`}>{n.title}</p>
                      {n.message && <p className="text-xs text-gray-500 truncate mt-0.5">{n.message}</p>}
                      <p className="text-[11px] text-gray-400 mt-1">{timeAgo(n.created_at)}</p>
                    </div>
                    {!n.is_read && <span className="w-2 h-2 rounded-full bg-indigo-500 mt-2 flex-shrink-0" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
